import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useToast } from 'hooks/use-toast';
import {
  APPROVAL_GRANTS_QUERY_KEY,
  useGetApprovalGrants,
  useRevokeApprovalGrant,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IApprovalGrant } from 'features/chat/agent-chat-module/services/gpt-chat.service';

/**
 * Tool Permissions Tab
 *
 * Lists the standing "Always allow" grants the user handed out from HIL approval
 * cards, grouped by tool, and lets them revoke one at a time.
 *
 * @param tabId - The ID of the current tab (used for consistency with other tabs)
 * @returns A standing tool-permission management tab
 */

interface ToolPermissionsTabProps {
  tabId: string;
}

interface ToolGroup {
  toolId: string;
  toolName: string;
  grants: IApprovalGrant[];
}

const groupByTool = (grants: IApprovalGrant[]): ToolGroup[] => {
  const groups = new Map<string, ToolGroup>();
  for (const grant of grants) {
    const group = groups.get(grant.toolId);
    if (group) {
      group.grants.push(grant);
    } else {
      groups.set(grant.toolId, {
        toolId: grant.toolId,
        toolName: grant.toolName,
        grants: [grant],
      });
    }
  }
  return [...groups.values()];
};

const formatDate = (value: string) => {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value : new Date(ms).toLocaleDateString();
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ToolPermissionsTab: React.FC<ToolPermissionsTabProps> = ({ tabId }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: grants, isLoading } = useGetApprovalGrants();
  const { mutateAsync: revokeGrant } = useRevokeApprovalGrant();

  const groups = useMemo(() => groupByTool(grants ?? []), [grants]);

  const handleRevoke = async (grant: IApprovalGrant) => {
    const previous = queryClient.getQueryData<IApprovalGrant[]>(APPROVAL_GRANTS_QUERY_KEY);
    queryClient.setQueryData<IApprovalGrant[]>(APPROVAL_GRANTS_QUERY_KEY, (current) =>
      (current ?? []).filter((item) => item.id !== grant.id)
    );
    try {
      await revokeGrant({ grantId: grant.id });
      toast({
        variant: 'success',
        title: t('PERMISSION_REVOKED', 'Permission revoked'),
        description: t('PERMISSION_REVOKED_DESCRIPTION', 'This tool will ask for approval again.'),
      });
    } catch (error) {
      queryClient.setQueryData(APPROVAL_GRANTS_QUERY_KEY, previous);
      toast({
        variant: 'destructive',
        title: t('PERMISSION_REVOKE_FAILED', 'Could not revoke permission'),
        description: t('PLEASE_TRY_AGAIN', 'Please try again.'),
      });
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8 sm:py-12">
          <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (groups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border bg-card py-8 sm:py-12">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <p className="max-w-sm text-center text-xs sm:text-sm text-muted-foreground">
            {t(
              'NO_STANDING_PERMISSIONS',
              'No standing permissions. Approvals you grant with Always allow appear here.'
            )}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.toolId} className="rounded-lg border bg-card">
            <div className="border-b px-3 py-2 sm:px-4 sm:py-3">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground">{group.toolName}</h3>
            </div>
            <ul>
              {group.grants.map((grant) => (
                <li
                  key={grant.id}
                  className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0 sm:px-4 sm:py-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-xs sm:text-sm text-foreground">
                      {grant.actionName === '*'
                        ? t('ALL_ACTIONS', 'All actions')
                        : grant.actionName}
                    </span>
                    <span className="text-[11px] sm:text-xs text-muted-foreground">
                      {t('GRANTED_ON', 'Granted on')} {formatDate(grant.createdDate)}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleRevoke(grant)}>
                    {t('REVOKE', 'Revoke')}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 max-w-4xl">
        <div className="space-y-6 sm:space-y-8">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-1">
              {t('TOOL_PERMISSIONS', 'Tool permissions')}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t(
                'TOOL_PERMISSIONS_DESCRIPTION',
                'Tools you allowed to run without asking for approval each time.'
              )}
            </p>
          </div>

          {renderBody()}
        </div>
      </div>
    </div>
  );
};
